import React from 'react';
import { useChartColors, money } from './theme';

// Part-to-whole with two or three slices: one horizontal stacked bar, direct-labeled.
// A 2px surface gap separates the segments so adjacent fills never blur into one.
function StackedBar({ segments, total }) {
  const c = useChartColors();
  if (!total) return <div className="text-xs text-gray-500">Nothing in this period.</div>;
  return (
    <>
      <div className="flex h-2.5 rounded-full overflow-hidden mb-3" style={{ background: c.track }}>
        {segments.filter((s) => s.value > 0).map((s, i) => (
          <div key={s.key} style={{
            width: `${(s.value / total) * 100}%`,
            background: s.color,
            marginLeft: i === 0 ? 0 : 2,
          }} />
        ))}
      </div>
      <ul className="space-y-1.5">
        {segments.map((s) => (
          <li key={s.key} className="flex items-center gap-2 text-xs">
            {/* The swatch carries identity; the text stays in ink, never the series color. */}
            <span aria-hidden="true" className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
            <span className="text-gray-700 dark:text-gray-300">{s.label}</span>
            <span className="ml-auto tabular-nums text-gray-500">
              {s.value} · {Math.round((s.value / total) * 100)}%{s.note ? ` · ${s.note}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

export function SourceMix({ bySource }) {
  const c = useChartColors();
  const catalog = bySource?.catalog || { count: 0, revenue_cents: 0 };
  const concept = bySource?.concept || { count: 0, revenue_cents: 0 };
  const total = catalog.count + concept.count;
  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded-xl p-4" aria-label="Orders by source">
      <h3 className="text-sm font-semibold mb-3">Where orders come from</h3>
      <StackedBar total={total} segments={[
        { key: 'catalog', label: 'Catalog class', value: catalog.count, color: c.series, note: money(catalog.revenue_cents) },
        { key: 'concept', label: 'Commissioned concept', value: concept.count, color: c.alt, note: money(concept.revenue_cents) },
      ]} />
    </section>
  );
}

export function StateMix({ byState }) {
  const c = useChartColors();
  const s = byState || {};
  const get = (k) => s[k] || { count: 0, revenue_cents: 0 };
  const total = get('collected').count + get('awaiting').count + get('lost').count;
  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded-xl p-4" aria-label="Orders by state">
      <h3 className="text-sm font-semibold mb-3">Order states</h3>
      <StackedBar total={total} segments={[
        { key: 'collected', label: 'Collected', value: get('collected').count, color: c.collected, note: money(get('collected').revenue_cents) },
        { key: 'awaiting', label: 'Awaiting payment', value: get('awaiting').count, color: c.awaiting, note: money(get('awaiting').revenue_cents) },
        { key: 'lost', label: 'Canceled or refunded', value: get('lost').count, color: c.lost, note: money(get('lost').revenue_cents) },
      ]} />
    </section>
  );
}
